import { ReactNode } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import type { LunimarRealm } from "@/components/lunimar/lunimarRealms";

interface Props {
  realm?: LunimarRealm;
  /** Short title — e.g. "Lumi's take" or "Signal flag". */
  title?: string;
  /** Body content, ideally one or two sentences. */
  children: ReactNode;
  /** Tag like "Insight", "Warning", "Confirmed". */
  tag?: string;
}

/**
 * Guide signal card — used to surface educational/coach signals after a
 * decision. Distinct from ConsequencePanel: this is the *voice* of Lumi.
 */
const LumiSignalCard = ({ realm, title = "Lumi", children, tag }: Props) => {
  const accent = realm?.secondaryAccent ?? "262 78% 64%";
  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.05 }}
      className="relative overflow-hidden rounded-xl border p-3 backdrop-blur-md sm:p-4"
      style={{
        borderColor: `hsl(${accent} / 0.35)`,
        background: `linear-gradient(135deg, hsl(${accent} / 0.10), hsl(228 45% 12% / 0.6))`,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
          style={{
            background: `hsl(${accent} / 0.18)`,
            color: `hsl(${accent})`,
            boxShadow: `0 0 18px hsl(${accent} / 0.35)`,
          }}
        >
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div
              className="font-heading text-xs font-semibold uppercase tracking-wider"
              style={{ color: `hsl(${accent})` }}
            >
              {title}
            </div>
            {tag && (
              <span
                className="rounded-sm border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider"
                style={{
                  borderColor: `hsl(${accent} / 0.4)`,
                  color: `hsl(${accent})`,
                }}
              >
                {tag}
              </span>
            )}
          </div>
          <div className="mt-1 text-xs leading-relaxed text-white/80 sm:text-[13px]">
            {children}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default LumiSignalCard;